import { Check, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { VehicleFilters } from '@/components/VehicleFilters';
import type { ComponentProps } from 'react';

type VehicleFiltersProps = ComponentProps<typeof VehicleFilters>;

interface InventoryFiltersDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  previewCount: number;
  buttonColor: string;
  hasPendingFilters: boolean;
  onClearPending: () => void;
  onApply: () => void;
  filterProps: VehicleFiltersProps;
}

export function InventoryFiltersDrawer({
  open,
  onOpenChange,
  searchTerm,
  onSearchTermChange,
  previewCount,
  buttonColor,
  hasPendingFilters,
  onClearPending,
  onApply,
  filterProps,
}: InventoryFiltersDrawerProps) {
  const countLabel =
    previewCount === 1 ? 'veículo encontrado' : 'veículos encontrados';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="wl6-inv-drawer p-0 gap-0 flex flex-col h-full max-h-dvh w-full sm:max-w-[420px] overflow-hidden border-0 bg-white [&>button]:hidden"
      >
        <header className="wl6-inv-drawer__header shrink-0">
          <div className="wl6-inv-drawer__header-top">
            <div className="min-w-0">
              <h2 className="wl6-inv-drawer__title">Mais filtros</h2>
              <p className="wl6-inv-drawer__subtitle">
                {previewCount} {countLabel}
              </p>
            </div>
            <button
              type="button"
              className="wl6-inv-drawer__close"
              onClick={() => onOpenChange(false)}
              aria-label="Fechar filtros"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="relative mt-3">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
              aria-hidden
            />
            <Input
              placeholder="Digite aqui sua pesquisa..."
              value={searchTerm}
              onChange={(e) => onSearchTermChange(e.target.value)}
              className="pl-9 pr-9 h-10 w-full text-sm"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => onSearchTermChange('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Limpar busca"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </header>

        <div
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-5 py-3 sm:px-6"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'hsl(var(--muted-foreground) / 0.3) transparent',
          }}
        >
          <VehicleFilters {...filterProps} hideSearch hideClearAll />
        </div>

        <footer className="wl6-inv-drawer__footer shrink-0">
          <button
            type="button"
            className="wl6-inv-drawer__btn wl6-inv-drawer__btn--outline"
            onClick={onClearPending}
            disabled={!hasPendingFilters}
          >
            Limpar
          </button>
          <button
            type="button"
            className="wl6-inv-drawer__btn wl6-inv-drawer__btn--primary"
            style={{ backgroundColor: buttonColor }}
            onClick={onApply}
          >
            <Check className="h-4 w-4" />
            Aplicar filtros
          </button>
        </footer>
      </SheetContent>
    </Sheet>
  );
}
